import React, { useState } from "react";
import { X } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { supabase } from "../supabase/supabase";
import { ModalPortal, useLockBodyScroll } from "./sheets/ModalPortal";
import { SUPPORT_EMAIL } from "../data/legalInfo";
import packageJson from "../../package.json";

// Send a bug report or a suggestion from inside the app.
//
// The only route in was the support address, which is fine for a conversation
// and poor for a report: it arrives with no account, no build number and no
// platform, so every reply starts by asking for all three. Those are attached
// here instead, because the sender should not have to know them.
//
// The email is still offered underneath. Some people would rather write an
// email, and a support page that only works through a form is worse than one
// that offers both.
const KINDS = [
  { value: "bug", label: "Something is broken" },
  { value: "idea", label: "I have a suggestion" },
  { value: "other", label: "Something else" },
];

const MAX = 2000;

function FeedbackModal({ onClose }) {
  // Portalled to the body, like EditProfileModal. Rendered in place it sat
  // inside the nav, whose ancestor carries a transform, and a transformed
  // ancestor makes position: fixed resolve against that element rather than
  // the viewport. The overlay landed in the document flow above the page and
  // scrolled out of view with it.
  useLockBodyScroll(true);

  const [kind, setKind] = useState("bug");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async () => {
    const text = message.trim();
    if (!text || sending) return;

    setSending(true);
    setError("");

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("You need to be signed in to send feedback.");

      // supabase-js resolves with { error } rather than throwing, so an
      // unchecked write here would report success whatever happened.
      const { error: insertError } = await supabase.from("feedback").insert({
        user_id: user.id,
        kind,
        message: text,
        app_version: packageJson.version,
        platform: Capacitor.isNativePlatform() ? Capacitor.getPlatform() : "web",
      });
      if (insertError) throw insertError;

      setSent(true);
    } catch (err) {
      console.error("Feedback failed to send:", err);
      setError("That did not send. Try again, or email us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <ModalPortal open>
      <div
        className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center sm:p-4"
        onClick={onClose}
      >
      <div
        className="w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl flex flex-col"
        style={{ backgroundColor: "var(--color-bg-card)", maxHeight: "92vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between px-4 py-3.5 border-b flex-shrink-0"
          style={{ borderColor: "var(--color-border)" }}
        >
          <h2 className="text-base font-bold" style={{ color: "var(--color-text-strong)" }}>
            Send feedback
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex items-center justify-center rounded-full"
            style={{ width: 32, height: 32, backgroundColor: "var(--color-bg-page)" }}
          >
            <X size={18} style={{ color: "var(--color-text-muted)" }} />
          </button>
        </div>

        <div
          className="px-4 py-4 overflow-y-auto"
          style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
        >
          {sent ? (
            <>
              <p className="text-sm mb-2" style={{ color: "var(--color-text-strong)" }}>
                Thanks. That has reached us.
              </p>
              <p className="text-sm mb-4" style={{ color: "var(--color-text-muted)" }}>
                We read everything, and we will get back to you if we need more detail.
              </p>
              <button
                onClick={onClose}
                className="w-full rounded-xl py-3 text-sm font-semibold text-white"
                style={{ backgroundColor: "var(--color-brand)" }}
              >
                Done
              </button>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-1.5 mb-4">
                {KINDS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setKind(option.value)}
                    className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-left"
                    style={{
                      border: `1.5px solid ${kind === option.value ? "var(--color-brand)" : "var(--color-border)"}`,
                      backgroundColor: kind === option.value ? "var(--color-band-bg)" : "var(--color-bg-card)",
                      color: "var(--color-text-strong)",
                      fontWeight: kind === option.value ? 600 : 500,
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MAX))}
                rows={5}
                placeholder="What happened, or what would you like to see?"
                className="w-full rounded-xl px-3 py-2.5 text-sm"
                style={{ border: "1.5px solid var(--color-border)", color: "var(--color-text-strong)", resize: "vertical" }}
              />
              <div className="flex items-center justify-between mt-1.5 mb-3">
                <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                  Your account and app version are included.
                </span>
                <span className="text-xs" style={{ color: message.length > MAX - 100 ? "var(--color-danger-text)" : "var(--color-text-faint)" }}>
                  {message.length}/{MAX}
                </span>
              </div>

              {error && (
                <div
                  role="alert"
                  className="rounded-lg px-3 py-2 mb-3 text-sm"
                  style={{ backgroundColor: "var(--color-danger-bg)", border: "1px solid var(--color-danger-border)", color: "var(--color-danger-text)" }}
                >
                  {error}
                </div>
              )}

              <button
                onClick={submit}
                disabled={!message.trim() || sending}
                className="w-full rounded-xl py-3 text-sm font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: "var(--color-brand)" }}
              >
                {sending ? "Sending…" : "Send"}
              </button>

              <p className="text-xs text-center mt-3" style={{ color: "var(--color-text-muted)" }}>
                Or email {SUPPORT_EMAIL}
              </p>
            </>
          )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

export default FeedbackModal;
